// CSV order: read a "Product name, Qty" list and match each line to the catalogue.
// Same rules as the Peoples CSV order assistant: saved match -> exact name -> scored suggestions.

export const normalize = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
const spaces = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
export const firstWord = (value) => String(value ?? '').trim().split(/\s+/)[0] || '';
export const quantity = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};
const nameCompare = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

// Quoted fields, "" escapes, CRLF/LF and a UTF-8 BOM are handled; blank lines are skipped.
export function parseCsv(text) {
  const data = []; let row = []; let field = ''; let quoted = false;
  const source = String(text ?? '').replace(/^﻿/, '');
  const pushRow = () => { row.push(field.trim()); if (row.some((v) => v !== '')) data.push(row); row = []; field = ''; };
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];
    if (ch === '"') {
      if (quoted && source[i + 1] === '"') { field += '"'; i += 1; } else quoted = !quoted;
    } else if (ch === ',' && !quoted) { row.push(field.trim()); field = ''; }
    else if ((ch === '\n' || ch === '\r') && !quoted) { if (ch === '\r' && source[i + 1] === '\n') i += 1; pushRow(); }
    else field += ch;
  }
  pushRow();
  return data;
}

// First row is the header; the first two columns are product name and quantity. Repeated names are merged.
export function csvLines(text) {
  const table = parseCsv(text);
  if (table.length < 2) throw new Error('The CSV must contain a header row and at least one product.');
  const found = new Map();
  table.slice(1).forEach((line) => {
    const name = spaces(line[0]); const qty = quantity(line[1]);
    if (!name || !qty) return;
    const key = normalize(name).slice(0, 190); // same cap as the server's csv_key
    if (!key) return;
    if (found.has(key)) found.get(key).qty += qty;
    else found.set(key, { key, csvProduct: name, qty });
  });
  if (!found.size) throw new Error('No valid product and quantity rows were found in the first two columns.');
  return [...found.values()];
}

// Lookups built once per catalogue.
export function buildIndex(products) {
  const byId = new Map(); const exact = new Map(); const prefix = new Map(); const searchPrefix = new Map();
  const add = (map, key, product) => { if (!key) return; if (!map.has(key)) map.set(key, []); map.get(key).push(product); };
  (products || []).forEach((product) => {
    byId.set(String(product.product_id), product);
    add(exact, normalize(product.product_name), product);
    add(prefix, normalize(firstWord(product.product_name)).slice(0, 3), product);
    add(searchPrefix, spaces(product.product_name).toLowerCase().slice(0, 3), product);
  });
  return { byId, exact, prefix, searchPrefix, all: products || [] };
}

const FORM_CANON = { syp: 'syrup', syps: 'syrup', syr: 'syrup', syrp: 'syrup', syrup: 'syrup', syrups: 'syrup', sirup: 'syrup', sirop: 'syrup', serup: 'syrup', tab: 'tab', tabs: 'tab', tb: 'tab', tablet: 'tab', tablets: 'tab', cap: 'cap', caps: 'cap', capsule: 'cap', capsules: 'cap', capsul: 'cap', inj: 'inj', injection: 'inj', susp: 'susp', suspension: 'susp', drop: 'drop', drops: 'drop', drp: 'drop', drps: 'drop', oint: 'oint', ointment: 'oint', cream: 'cream', crm: 'cream', creme: 'cream', gel: 'gel', jel: 'gel', lotion: 'lotion', lotn: 'lotion', soln: 'soln', solution: 'soln', powder: 'powder', pwd: 'powder', sachet: 'sachet', sachets: 'sachet', satchet: 'sachet', spray: 'spray', sprays: 'spray', respules: 'respules', respule: 'respules', rotacaps: 'rotacaps', rotacap: 'rotacaps', kit: 'kit', kits: 'kit', mouthwash: 'mouthwash', gargle: 'gargle', tube: 'tube' };
const FORM_SET = new Set(Object.values(FORM_CANON));
const FORM_FUZZY = { syrup: ['syrup'], tab: ['tablet'], cap: ['capsule'], drop: ['drops', 'drop'], inj: ['injection'], susp: ['suspension'], cream: ['cream'], oint: ['ointment'], lotion: ['lotion'], spray: ['spray'], powder: ['powder'], sachet: ['sachet'], soln: ['solution'], respules: ['respules'], rotacaps: ['rotacaps'], mouthwash: ['mouthwash'], gargle: ['gargle'] };

function editDistance(a, b) {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 0; j <= b.length; j += 1) d[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) for (let j = 1; j <= b.length; j += 1) {
    d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  }
  return d[a.length][b.length];
}
function formOf(word, isFirst) {
  if (FORM_CANON[word]) return FORM_CANON[word];
  if (isFirst || word.length < 4 || /\d/.test(word)) return null;
  for (const [form, hints] of Object.entries(FORM_FUZZY)) {
    if (hints.some((h) => h.length >= 4 && Math.abs(h.length - word.length) <= 1 && editDistance(word, h) <= 1)) return form;
  }
  return null;
}
// "500MG" -> "500"; units and pack counts ("10S") are dropped; dosage forms become one canonical word.
function tokens(text) {
  return String(text ?? '').toLowerCase().split(/\s+/).map(normalize).filter(Boolean)
    .map((w) => { const m = /^(\d+(?:\.\d+)?)(mg|ml|gm|gms|mcg|g|kg|iu|l)$/.exec(w); return m ? m[1] : w; })
    .filter((w) => w && !/^(mg|ml|gm|gms|mcg|g|kg|iu)$/.test(w) && !/^\d+s$/.test(w))
    .map((w, i) => formOf(w, i === 0) || w);
}
const kind = (w) => (FORM_SET.has(w) ? 'form' : (/^\d/.test(w) ? 'num' : 'word'));
const formsIn = (list) => list.filter((w) => kind(w) === 'form');
const tokenIn = (w, list) => list.some((x) => x === w || (w.length >= 3 && x.length >= 3 && (x.startsWith(w) || w.startsWith(x))));

export function scoreProduct(csvName, productName) {
  const a = tokens(csvName).filter((w) => kind(w) !== 'form');
  const b = tokens(productName).filter((w) => kind(w) !== 'form');
  if (!a.length || !b.length) return 0;
  const first = a[0] === b[0] ? 1 : ((a[0].length >= 3 && b[0].length >= 3 && (a[0].startsWith(b[0]) || b[0].startsWith(a[0]))) ? 0.72 : 0);
  if (!first) return 0;
  const coverage = a.filter((w) => tokenIn(w, b)).length / a.length;
  const extra = b.reduce((sum, w) => sum + (w !== b[0] && !tokenIn(w, a) ? (kind(w) === 'num' ? 0.18 : 0.1) : 0), 0);
  return Math.max(0, Math.min(1, first * (0.4 + 0.6 * coverage) - extra));
}
export const confidence = (score) => (score >= 0.82 ? 'High' : score >= 0.6 ? 'Medium' : 'Low');

// Up to three suggestions, preferring the same dosage form; otherwise the nearest other form, flagged.
export function suggestions(csvName, index) {
  const pool = index.prefix.get(normalize(firstWord(csvName)).slice(0, 3)) || [];
  const csvForms = formsIn(tokens(csvName));
  const scored = pool.map((product) => {
    const productForms = formsIn(tokens(product.product_name));
    const sameForm = !csvForms.length || !productForms.length || csvForms.some((f) => productForms.includes(f));
    return { product, score: scoreProduct(csvName, product.product_name), sameForm };
  }).filter((e) => e.score >= 0.5);
  const byScore = (x, y) => y.score - x.score || nameCompare(x.product.product_name, y.product.product_name);
  const same = scored.filter((e) => e.sameForm).sort(byScore).slice(0, 3);
  if (same.length) return same.map((e) => ({ ...e, level: confidence(e.score) }));
  return scored.filter((e) => !e.sameForm).sort(byScore).slice(0, 3).map((e) => ({ ...e, otherForm: true, level: confidence(Math.min(e.score, 0.7)) }));
}

// Manual search: product names starting with what was typed (from the 3-letter bucket for speed).
export function searchProducts(query, index, limit = 60) {
  const q = spaces(query).toLowerCase();
  if (q.length < 2) return [];
  const source = q.length >= 3 ? (index.searchPrefix.get(q.slice(0, 3)) || []) : index.all;
  const starts = source.filter((p) => spaces(p.product_name).toLowerCase().startsWith(q));
  const results = starts.length ? starts : index.all.filter((p) => spaces(p.product_name).toLowerCase().includes(q));
  return results.sort((a, b) => nameCompare(a.product_name, b.product_name)).slice(0, limit);
}

// Saved match first, then a single exact-name match; anything else needs the user's choice.
export function autoMatch(line, index, savedMatches) {
  const saved = savedMatches?.[line.key];
  if (saved && index.byId.has(String(saved))) return { productId: String(saved), source: 'saved' };
  const exact = index.exact.get(line.key) || [];
  if (exact.length === 1) return { productId: String(exact[0].product_id), source: 'exact' };
  return null;
}
